/**
 * dealsReadState.ts — a deal thread the visitor has read is read on the server too.
 *
 * Blake's rulings of 2026-10-07 (Asana card 1219177682931853):
 *
 *   1. A thread is READ when its messages are on the visitor's screen. Two places
 *      show them:
 *        - THE DEALS PANE. Up to 1024px (a phone or a tablet) the stylesheet hides
 *          it unless a thread is SELECTED (styles.css, `@media (max-width: 1024px)`:
 *          `.inbox-grid .thread-view { display: none; }` and
 *          `.inbox-grid.thread-open .thread-view { display: flex; }`). renderDeals
 *          paints thread-open for a non-null dealsSel (a tapped row, a floor
 *          card, an offer or Activity row; on a phone a tab tap only filters
 *          the list and selects nothing), and the phone's Back arrow
 *          (#thread-back, app.js) clears dealsSel and repaints, so no row stays
 *          painted as the open one and no later render reopens it. So on a phone
 *          the pane is on screen by thread-open, never by dealsSel or the open
 *          row alone. Wider than that, the thread renderDeals opens by itself,
 *          with nothing selected, is on screen too. A selection counts only while
 *          its row is painted as the open one: one that is off the tab being
 *          shown (on a computer, Account > Orders keeps a selection from another
 *          tab) leaves the pane on renderDeals' fallback, a person nobody chose,
 *          and nothing is read. An accept or a decline that moves the selected
 *          deal no longer leaves it off the tab: the list read moves the tab
 *          with it (floorTransport.ts __gbzRefreshDeals, card 1219285769469932).
 *        - THE MESSAGE RAIL, which shows one person's messages over any page
 *          ([data-msg], [data-msgitem] and the collection tile's [data-msgcoll]
 *          open it).
 *      While the rail is open it is the ONLY surface on screen: the Deals pane
 *      under it does not count (Blake 2026-10-09), so a line that lands in the
 *      pane under it stays unread until the rail closes and the pane is
 *      actually visible. Every open of the rail is a full render (the three
 *      openers, rerenderKeepScroll). Of the closes, four render at once: the ✕
 *      and the backdrop (rerenderKeepScroll, the page stays), the "open it in
 *      Deals" button (render(), to Deals) and the composer's Make Offer
 *      (render(), to the Exchange). Two render only through the hash change
 *      they cause: the head's avatar and an item-link card clear msgRailFor and
 *      set the hash; until that render (one task), or for good when the hash is
 *      unchanged, the node stays painted and still shows that person while
 *      msgRailFor already names nobody. Nothing is read while it stands (the
 *      pane under it is covered), and the next render of any cause removes it.
 *      So after any close that leaves the Deals page on screen, the observer's
 *      sweep reads the pane.
 *      A line from the other person that lands while it is on screen is read as
 *      well (the open-thread poll brings it to the pane, dealThreadPoll.ts).
 *      Nothing is read while the tab is hidden.
 *   2. The server is told: PATCH /api/conversations/:id/read, no body (it also
 *      reads that person's new_message notifications). It marks the whole
 *      conversation, so it is told from what is painted (Blake 2026-10-08: as the
 *      app does), never ahead of it (THE HOLD, below). The conversation id is not
 *      on the list rows: it arrives with the thread's events response and the
 *      bridge keeps it on the rows. A pair that has never spoken has none, and
 *      nothing to mark; a person whose thread was never loaded this visit has
 *      none yet either (a rail opened for them shows their offers until the
 *      open-thread poll's read on opening it lands), and is cleared in memory
 *      only until it arrives.
 *   3. The unread dot clears AT ONCE on every deal row of that person, in memory.
 *      (Blake's own open handlers clear only the clicked row, and the next list
 *      read brought it back.) A refusal or an unreachable server is said nowhere
 *      and not retried: the next list read restores the server's truth.
 *   (4, the badge's rule, is dealsBadgeCount in app.js.)
 *
 * WHEN THE SERVER IS TOLD. Per person, the seam keeps whether the server has been
 * told this visit, the newest line of theirs the last PATCH covered, and whether
 * a PATCH is owed. One is owed when:
 *   - a click on one of their rows, or on a rail opener for them, clears a dot;
 *   - a look after a paint finds them on screen with a dot, and clears it;
 *   - a look after a paint finds them on screen with their conversation id
 *     known, and they were never told, or a line from them newer than the last
 *     PATCH covered is painted.
 * A PATCH goes only when ALL of these hold at the moment it would go, and that
 * includes the one that goes because something was owed while an earlier PATCH
 * of theirs was out (it goes when that one answers, once, or not at all):
 *   1. no PATCH of theirs is on the wire;
 *   2. the tab is visible and the guided tour is not running;
 *   3. they are on a visible surface now: the rail while it is open, else the Deals pane;
 *   4. their conversation id is known;
 *   5. THE HOLD, below.
 * What is owed stays owed until a PATCH goes. A failure owes nothing by itself,
 * and nothing is retried on a timer.
 * This is how the app marks read: when a thread screen opens it marks the
 * conversation as soon as it has the conversation id, from its cache of an
 * earlier read when it has one (deal-thread/[key].tsx:301-312, the query at
 * :263-272; that cache is fresh for 30 s and kept 5 min, app/_layout.tsx:
 * 227-245). Neither client reads the thread again before marking it.
 *
 * 🔴 THE HOLD. What is painted is the newest counted line from them on the
 * surface showing them now, the rail while it is open (it covers the page),
 * else the pane. Each surface's own painted lines, not their rows: a filtered tab
 * in the pane paints only that tab's rows of theirs, and a rail shows what it
 * painted until it is painted again (floorTransport.ts __gbzPaintedReceivedMs).
 * Nothing is sent while the list already knows of a newer line from them
 * (lastReceivedMs, the row's lastReceivedAt) than that; with none of their lines
 * painted, any line the list knows holds it. The dot still clears at once. In
 * the Deals pane, and in an open rail, the open-thread poll paints that line
 * within a beat (a rail is read the moment it opens, too), and a line painted
 * while they are on screen is told as above. For a visitor who leaves first,
 * nothing is sent, and the dot comes back with the next list
 * read. The two sides count the same lines, or the hold would never let
 * go: the server's lastReceivedAt skips an "[Offer: " chip and the acceptance DM
 * (deal-threads.ts isHiddenSystemLine), and the painted side skips the two lines
 * the website paints that the server never counts, an offer's typed note and the
 * stand-in words for a chip whose offer is not in the thread (mapThreadDetail
 * marks both).
 *
 * ⚠️ THE KNOWN LIMIT, shared with the app. A line they sent after their thread
 * was last loaded, with no list read since, is unknown to both sides here: a
 * visitor who opens the thread and leaves before the poll's beat has it marked
 * read without having seen it. The exact fix, marking read only up to the last
 * message seen, is a server change.
 *
 * HOW IT SEES
 *   - a capture-phase click on a thread row, or on one of the three rail
 *     openers, clears that person's rows and owes the server BEFORE Blake's own
 *     handler re-renders, so that render already paints the badge right. It has
 *     to be the click: his handlers for a row, [data-msg] and [data-msgitem]
 *     clear one row of the person themselves, which for a person with one deal
 *     is every row, so the sweep after them would find nothing unread, and a dot
 *     that came back since they were told would never be told (adversarial pass
 *     3). A click that clears nothing owes nothing. The detail loader listens
 *     the same way, on the same rows;
 *   - a MutationObserver on #app re-checks what is on screen after every paint:
 *     the pane (the computer's default thread, a thread opened from anywhere, the
 *     events answer that brings the conversation id, the poll's new line, a list
 *     re-read) and the rail;
 *   - and once more when a hidden tab becomes visible, since nothing was read
 *     while it was hidden (the same visibilitychange the poll listens to).
 *
 * 🔴 IT NEVER SELECTS, AND IT NEVER HAS TO. With nothing selected the pane shows
 * the first row. Under the old unread-first sort, clearing that person's dot
 * dropped them below the next unread person, who then became the thread that
 * opened by itself and was read in turn: one visit read the whole list. The
 * Deals order (app.js dealsOrdered, Blake 2026-10-07) reads nothing that reading
 * changes, and no person's key goes down during a visit, from its first list read
 * on (the visitor's own counter takes the offer it answers off the wire; every
 * list read and every Deals paint keeps the highest key), so with nothing
 * selected the pane's person stays the first row, which is also the thread the
 * loader, the composer and the poll take. With a selection that is off the tab
 * being shown (a computer's Account > Orders keeps one from another tab; an
 * accept or a decline no longer leaves one, since the list read moves the tab
 * with the selected deal), the pane, those DOM readers and the composer all take
 * the fallback person (the composer sends to the person in the pane, card
 * 1219285769469932), and this seam reads no one. This seam never writes
 * dealsSel: it is what the visitor's own clicks made it, or, when a list read
 * drops the selected row, that person's newest row (floorTransport.ts
 * __gbzRefreshDeals).
 *
 * 🔴 A REPAINT NEVER COSTS A DRAFT, AND IT KEEPS A TOAST THAT IS SHOWING, FOR
 * THE DEFAULT LENGTH. Clearing a thread nobody clicked needs a repaint of its
 * own, and a repaint replaces #app wholesale. While a composer holds text, or
 * anything editable has focus, it is skipped: the rows are clear already, and
 * the next render paints them so. The toast is inside #app too
 * (renderShellExtras paints a fresh, empty #toast on every render), and it is
 * often what started this read: after an offer action in the rail on a page
 * where neither of that action's re-reads paints (the list install paints only
 * #/deals, #/inbox and the signed-in home page; the thread install only #/deals
 * and #/inbox), its toast is the one change on screen, the sweep it starts
 * clears a dot, and the repaint would take the confirmation of the visitor's
 * own action away as it appears. So a toast that is showing is shown again
 * after the repaint, with the page's own toast(), at its default length
 * (2.6 s): a caller's own longer length is not kept. Two callers ask for one
 * (wantsWrite and checkoutContext, LONG_TOAST_MS, 6 s); adversarial pass 10
 * found no flow that puts the seam's repaint under either (the rail's backdrop
 * covers the pages that show them), and one that did would cut that toast to
 * 2.6 s.
 *
 * 🔴 NOTHING DURING THE GUIDED TOUR. The tour puts its own threads into the
 * Deals list and selects one, under handles a real counterpart can have
 * (slabhouse, floorfinds, retrobyte, kickscloset: the tour expects real people
 * with those names, app.js tourInstallFixtures), and what the bridge knows is
 * per handle, so reading the tour's thread would clear and mark the real
 * person's. While it runs (__gbzTourRunning) no sweep and no click is acted on,
 * and no PATCH goes, not even one owed from before it began (rule 2 above).
 * exitTour removes the fixtures and ends the tour before its own paint, so that
 * paint reads as any other.
 */

import { paneThreadId } from "./floorTransport";

/** The stylesheet's breakpoint for the deals pane (pinned to styles.css by dealsReadState.test.ts). */
export const NARROW_LAYOUT_QUERY = "(max-width: 1024px)";

/** Where Blake opens a thread from: the Deals list's rows and the offer rows that jump to one. */
const THREAD_ROW_SELECTOR = "[data-selthread], [data-gothread]";
/** The avatar inside such a row. Its own handler stops the click and opens the profile instead. */
const ROW_AVATAR_SELECTOR = ".avatar[data-gouser]";
/** Where he opens the message rail from: a profile's Message, a listing's or a piece's, and a collection tile's. */
const RAIL_OPENER_SELECTOR = "[data-msg], [data-msgitem], [data-msgcoll]";
/** The two composers whose half-typed text a repaint would wipe. */
const COMPOSERS = ["#msg-in", "#msgrail-in"];
/** The rail's backdrop, painted by renderShellExtras inside #app whenever the rail is open; the class is static in its template. */
const RAIL_OPEN_SELECTOR = "#msgrail-back.open";

/** What the bridge knows about the person a thread belongs to, over all their rows (__gbzPersonReadFacts). */
interface PersonReadFacts {
  handle: string;
  unread: boolean;
  convoId: string | null;
  /** The newest line from them the LIST knows of (lastReceivedAt), epoch ms, 0 for none. */
  lastReceivedMs: number;
}

/** The two places a person's messages are painted. */
type Surface = "pane" | "rail";

interface ReadStateWindow extends Window {
  render?: () => void;
  rerenderKeepScroll?: () => void;
  /** data.js toast(): sets #toast's text, adds .show, and takes .show off again after ms (2600 by default). */
  toast?: (msg: string, ms?: number) => void;
  __gbzPersonReadFacts?: (threadId: string) => PersonReadFacts | null;
  /** The newest counted line from that thread's person on what the surface painted, epoch ms, or null. */
  __gbzPaintedReceivedMs?: (threadId: string, surface: Surface) => number | null;
  __gbzThreadHandle?: (threadId: string) => string | null;
  __gbzClearPersonUnread?: (threadId: string) => number;
  __gbzDealsSel?: () => string | null;
  __gbzMsgRailFor?: () => string | null;
  __gbzThreadIdForHandle?: (handle: string) => string | null;
  __gbzTourRunning?: () => boolean;
}

/** Per person: what the server has been told, and what it is owed. */
interface Mark {
  /** A thread id of theirs, to read their facts by. */
  threadId: string;
  /** Told at least once. Until then the first sight is owed on its own. */
  told: boolean;
  /** The newest line from them painted on screen when a PATCH last went with one painted; null until then. */
  toldThrough: number | null;
  /** Something the server has not been told yet. */
  owed: boolean;
  /** A PATCH for them is on the wire. */
  inFlight: boolean;
}

/**
 * The deal thread whose messages the Deals pane shows the visitor, or null.
 *
 * Stricter than paneThreadId, the detail loader's rule, which answers what is
 * PAINTED: a selection counts only while its row is painted as the open one
 * (.thread-row.on). Without that row the pane shows renderDeals' fallback for a
 * selection that fell out of the tab, a person the visitor never chose.
 *   narrow: the pane shows only while .inbox-grid has thread-open (a selection,
 *           until the Back arrow clears it and repaints), and then its row.
 *   wide:   the open row; otherwise the first row, but only when nothing is
 *           selected at all (the thread that opens by itself).
 * No matchMedia reads as narrow, the side that marks only what was selected.
 * While the side rail is open it covers the page (a fixed, full-viewport
 * backdrop), so the pane under it is not on screen (Blake 2026-10-09). That is
 * keyed on the rail's node, not on the rail's person, because the node is
 * painted for a person with no Deals row too, and because for one task after
 * the head's avatar or an item-link card is clicked (until the hashchange
 * render) it is still painted while msgRailFor is already null; a sweep in that
 * gap (a toast, for one: the hash already names another page, so a list read
 * does not repaint there) must still see the page covered (test EG).
 */
export function threadOnScreen(win: Window): string | null {
  const doc = win.document;
  if (doc.querySelector(RAIL_OPEN_SELECTOR)) return null;
  const narrow = typeof win.matchMedia === "function" ? win.matchMedia(NARROW_LAYOUT_QUERY).matches : true;
  if (!doc.querySelector(narrow ? ".inbox-grid.thread-open .thread-view" : ".inbox-grid .thread-view")) return null;
  const open = doc.querySelector<HTMLElement>(".thread-row.on[data-selthread]");
  if (open) return open.dataset.selthread ?? null;
  if (narrow || (win as ReadStateWindow).__gbzDealsSel?.()) return null;
  return paneThreadId(doc);
}

/**
 * The person whose messages the side rail shows, as one of their thread ids, or
 * null: the rail is painted open and names someone with rows in the Deals list.
 */
export function railOnScreen(win: Window): string | null {
  const w = win as ReadStateWindow;
  if (!win.document.querySelector(RAIL_OPEN_SELECTOR)) return null;
  const handle = w.__gbzMsgRailFor?.() ?? null;
  return handle ? w.__gbzThreadIdForHandle?.(handle) ?? null : null;
}

export function installDealsReadState(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): () => void {
  const w = win as ReadStateWindow;
  const doc = win.document;
  const marks = new Map<string, Mark>();
  let stopped = false;

  const markFor = (facts: PersonReadFacts, threadId: string): Mark => {
    let mark = marks.get(facts.handle);
    if (!mark) {
      mark = { threadId, told: false, toldThrough: null, owed: false, inFlight: false };
      marks.set(facts.handle, mark);
    }
    mark.threadId = threadId;
    return mark;
  };

  /* Clears every row of the person; owes the server a mark when a row changed. */
  const clear = (mark: Mark): boolean => {
    if ((w.__gbzClearPersonUnread?.(mark.threadId) ?? 0) === 0) return false;
    mark.owed = true;
    return true;
  };

  const drafting = (): boolean => {
    for (const selector of COMPOSERS) {
      if ((doc.querySelector(selector) as HTMLInputElement | null)?.value) return true;
    }
    const el = doc.activeElement as HTMLElement | null;
    return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
  };

  /* rerenderKeepScroll, not render: the visitor keeps their place, as the bridge's
     own late installs do (a scrolled Deals list keeps its scroll; render() would
     paint it back at the top). And it keeps a toast that is showing (header): a
     repaint replaces #toast with a fresh, empty one, so the words are taken before
     it (only while .show is on: after its time a toast keeps its words but is not
     showing) and shown again after it. Shown again, the toast's timer starts over
     at data.js's default, 2.6 s, whatever length its caller asked for (header). */
  const repaint = (): void => {
    if (drafting()) return;
    const showing = doc.querySelector("#toast.show")?.textContent;
    if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
    else w.render?.();
    if (showing) w.toast?.(showing);
  };

  /* The poll's own test (dealThreadPoll.ts): a background tab is nobody looking. */
  const visible = (): boolean => doc.visibilityState !== "hidden";

  /* The guided tour's threads share handles with real people (the header). */
  const touring = (): boolean => w.__gbzTourRunning?.() === true;

  /* The surface showing this person now (at most one can: the rail covers the
     pane while it is open, threadOnScreen), and the newest counted line from
     them it painted, or null when it painted none of their lines. The order of
     the two entries is immaterial and is kept rail-first only to read like the
     rule. */
  const onScreen = (handle: string): { shown: boolean; painted: number | null } => {
    const surfaces: Array<[string | null, Surface]> = [[railOnScreen(win), "rail"], [threadOnScreen(win), "pane"]];
    for (const [threadId, surface] of surfaces) {
      if (threadId && w.__gbzThreadHandle?.(threadId) === handle) {
        return { shown: true, painted: w.__gbzPaintedReceivedMs?.(threadId, surface) ?? null };
      }
    }
    return { shown: false, painted: null };
  };

  /* A PATCH for this person, only when every rule in the header holds now. */
  const send = (mark: Mark): void => {
    // 1. No PATCH of theirs on the wire.
    if (stopped || mark.inFlight) return;
    // 2. Somebody is looking.
    if (!visible() || touring()) return;
    const facts = w.__gbzPersonReadFacts?.(mark.threadId) ?? null;
    if (!facts) return;
    // 3. They are on a visible surface now.
    const screen = onScreen(facts.handle);
    if (!screen.shown) return;
    // 4. Their conversation id is known.
    if (!facts.convoId) return;
    // 5. THE HOLD (header): the list knows a newer line from them than the
    // surface showing them painted. What is owed stays owed.
    if (facts.lastReceivedMs > (screen.painted ?? 0)) return;
    const convoId = facts.convoId;
    mark.told = true;
    mark.owed = false;
    if (screen.painted !== null) mark.toldThrough = screen.painted;
    mark.inFlight = true;
    void Promise.resolve()
      .then(() => authFetch(`/api/conversations/${encodeURIComponent(convoId)}/read`, { method: "PATCH" }))
      .then((res) => {
        if (!res.ok) console.warn(`[web-install] deals: conversation ${convoId} not marked read (${res.status})`);
      })
      .catch((err) => {
        console.warn(`[web-install] deals: conversation ${convoId} not marked read:`, err);
      })
      .finally(() => {
        mark.inFlight = false;
        // What was owed while it was out goes now, through every rule above
        // again; the failure itself owes nothing.
        if (mark.owed) send(mark);
      });
  };

  /* One read for a person whose messages are on screen, wherever they show. */
  const read = (threadId: string): void => {
    const facts = w.__gbzPersonReadFacts?.(threadId) ?? null;
    if (!facts) return;
    const mark = markFor(facts, threadId);
    if (facts.unread && clear(mark)) repaint();
    if (!facts.convoId) return;
    const { painted } = onScreen(facts.handle);
    const newer =
      painted !== null && (mark.toldThrough === null || painted > mark.toldThrough);
    if (!mark.told || newer) mark.owed = true;
    if (mark.owed) send(mark);
  };

  const sweep = (): void => {
    if (!visible() || touring()) return;
    const pane = threadOnScreen(win);
    if (pane) read(pane);
    const rail = railOnScreen(win);
    if (rail) read(rail);
  };

  /* The person a rail opener names, as one of their thread ids: data-msg is the
     handle, data-msgitem "handle:listingId", data-msgcoll "handle|listingId|title"
     (app.js's own splits). */
  const openerThread = (opener: HTMLElement): string | null => {
    const handle =
      opener.dataset.msg ?? opener.dataset.msgitem?.split(":")[0] ?? opener.dataset.msgcoll?.split("|")[0] ?? null;
    return handle ? w.__gbzThreadIdForHandle?.(handle) ?? null : null;
  };

  /* The thread a click on a Deals row opens, or null for a click on the row's
     avatar, which opens the profile instead. */
  const rowThread = (target: Element | null): string | null => {
    const row = target?.closest?.(THREAD_ROW_SELECTOR) as HTMLElement | null;
    if (!row) return null;
    const avatar = target?.closest?.(ROW_AVATAR_SELECTOR);
    if (avatar && row.contains(avatar)) return null;
    return row.dataset.selthread ?? row.dataset.gothread ?? null;
  };

  const onClick = (event: Event): void => {
    if (!visible() || touring()) return;
    const target = event.target as Element | null;
    const opener = target?.closest?.(RAIL_OPENER_SELECTOR) as HTMLElement | null;
    const threadId = opener ? openerThread(opener) : rowThread(target);
    if (!threadId) return;
    const facts = w.__gbzPersonReadFacts?.(threadId) ?? null;
    if (facts?.unread) clear(markFor(facts, threadId));
  };

  /* Back from the background: whatever is on screen now is read now. Hiding needs
     no handler, since every sweep asks first. */
  const onVisibility = (): void => {
    if (visible()) sweep();
  };

  doc.addEventListener("click", onClick, true);
  doc.addEventListener("visibilitychange", onVisibility);
  const app = doc.getElementById("app");
  const observer = app ? new MutationObserver(() => sweep()) : null;
  observer?.observe(app!, { childList: true, subtree: true });
  sweep();

  return () => {
    stopped = true;
    doc.removeEventListener("click", onClick, true);
    doc.removeEventListener("visibilitychange", onVisibility);
    observer?.disconnect();
  };
}
